import React from 'react';
import SingleDatePicker from './SingleDatePicker';
import SingleTimePicker from './SingleTimePicker';

const pad = (value) => String(value).padStart(2, '0');

const getDatePart = (value) => (value ? String(value).slice(0, 10) : '');
const getTimePart = (value) => {
  const raw = value ? String(value) : '';
  return raw.includes('T') ? raw.slice(11, 16) : '';
};

const combineDateTime = (date, time) => {
  if (!date) return '';
  return `${date}T${time || '00:00'}`;
};

const getNowParts = () => {
  const now = new Date();
  return {
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  };
};

const getDefaultTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 2);
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

function DateTimePicker({
  id,
  name,
  value,
  onChange,
  className = 'ui-input',
  placeholder = 'Select date',
  minDateTime,
  maxDateTime,
  disabled = false,
  style,
}) {
  const fieldName = name || id;
  const dateValue = getDatePart(value);
  const timeValue = getTimePart(value);
  const maxDate = getDatePart(maxDateTime);

  const now = getNowParts();
  const providedMinDate = getDatePart(minDateTime);
  // Default floor is "today" so past dates can't be picked; honor a later caller-provided min.
  const minDate = providedMinDate && providedMinDate > now.date ? providedMinDate : now.date;

  // Restrict time only when the chosen date is the earliest allowed date.
  let minTime;
  if (dateValue && dateValue === minDate) {
    const providedMinTime = getTimePart(minDateTime);
    if (minDate === now.date) {
      minTime = providedMinTime && providedMinTime > now.time ? providedMinTime : now.time;
    } else {
      minTime = providedMinTime || undefined;
    }
  }

  const emitChange = (nextValue) => {
    onChange?.({
      target: {
        id,
        name: fieldName,
        value: nextValue,
      },
    });
  };

  const handleDateChange = (event) => {
    // Default the time to "now + 2 min" instead of 00:00 when no time is chosen yet.
    emitChange(combineDateTime(event.target.value, timeValue || getDefaultTime()));
  };

  const handleTimeChange = (event) => {
    if (!dateValue) return;
    emitChange(combineDateTime(dateValue, event.target.value));
  };

  return (
    <div className="tw-flex tw-gap-2">
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <SingleDatePicker
          id={id ? `${id}-date` : undefined}
          name={`${fieldName}_date`}
          value={dateValue}
          onChange={handleDateChange}
          className={className}
          placeholder={placeholder}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          style={style}
        />
      </div>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <SingleTimePicker
          className={className}
          name={`${fieldName}_time`}
          value={timeValue}
          onChange={handleTimeChange}
          disabled={disabled || !dateValue}
          minTime={minTime}
          style={style}
        />
      </div>
    </div>
  );
}

export default DateTimePicker;
